import React, { useEffect, useState } from 'react';
import Header from './Header';
import Sidebar from './Sidebar';
import { resolveRoleName } from '../../utils/auth';

// Below this width (1280px screens at 125% scaling, 1366px at 125%, ...) the
// full 240px rail leaves too little room for tables and canvases, so the rail
// starts as icons. The toggle still works; crossing the breakpoint resets it.
const COMPACT_SIDEBAR_QUERY = '(max-width: 1279px)';

// Phones have no room for even the icon rail, so below this width the sidebar
// becomes an off-canvas drawer opened from the header toggle. Keep in sync with
// the drawer media query in layout.css.
const MOBILE_NAV_QUERY = '(max-width: 767px)';

const matchesQuery = (query) =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia(query).matches;

const MainLayout = ({ user, onLogout, children, activeSection, setActiveSection }) => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => matchesQuery(COMPACT_SIDEBAR_QUERY));
  const [isMobileNav, setIsMobileNav] = useState(() => matchesQuery(MOBILE_NAV_QUERY));
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [roleName, setRoleName] = useState(null);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const compactMql = window.matchMedia(COMPACT_SIDEBAR_QUERY);
    const mobileMql = window.matchMedia(MOBILE_NAV_QUERY);
    const handleCompactChange = (event) => setSidebarCollapsed(event.matches);
    const handleMobileChange = (event) => {
      setIsMobileNav(event.matches);
      setMobileNavOpen(false);
    };
    compactMql.addEventListener('change', handleCompactChange);
    mobileMql.addEventListener('change', handleMobileChange);
    return () => {
      compactMql.removeEventListener('change', handleCompactChange);
      mobileMql.removeEventListener('change', handleMobileChange);
    };
  }, []);

  // Navigating from the drawer should reveal the page it opened.
  useEffect(() => {
    setMobileNavOpen(false);
  }, [activeSection]);

  // Escape closes the drawer, matching the backdrop tap.
  useEffect(() => {
    if (!mobileNavOpen) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setMobileNavOpen(false);
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [mobileNavOpen]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (!user) { setRoleName(null); return; }
        const name = await resolveRoleName(user);
        if (!cancelled) setRoleName(name || null);
      } catch {
        if (!cancelled) setRoleName(null);
      }
    })();
    return () => { cancelled = true; };
  }, [user]);

  // Tapping the current page in the drawer doesn't change activeSection, so
  // close the drawer on every navigation rather than only on section changes.
  const handleNavigate = (section) => {
    setMobileNavOpen(false);
    setActiveSection(section);
  };

  const handleToggleSidebar = () => {
    if (isMobileNav) setMobileNavOpen((open) => !open);
    else setSidebarCollapsed(!sidebarCollapsed);
  };

  return (
    <div className="dashboard-layout">
      <Header
        user={user}
        onLogout={onLogout}
        onToggleSidebar={handleToggleSidebar}
        sidebarExpanded={isMobileNav ? mobileNavOpen : !sidebarCollapsed}
      />
      <div className="dashboard-main">
        <Sidebar
          collapsed={isMobileNav ? false : sidebarCollapsed}
          mobileOpen={isMobileNav && mobileNavOpen}
          onExpand={() => setSidebarCollapsed(false)}
          activeSection={activeSection}
          setActiveSection={handleNavigate}
          roleName={roleName}
          user={user}
        />
        {isMobileNav && mobileNavOpen && (
          <div
            className="sidebar-backdrop"
            onClick={() => setMobileNavOpen(false)}
            aria-hidden="true"
          />
        )}
        <main className="dashboard-content">
          {children}
        </main>
      </div>
    </div>
  );
};

export default MainLayout;
